import React, { useState } from 'react';
import { Box, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import LineWeightIcon from '@mui/icons-material/LineWeight';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import OpacityIcon from '@mui/icons-material/Opacity';
import CropIcon from '@mui/icons-material/Crop';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { COPY } from '../presentationCopy';
import { SlideObject, resolvedLineHeight } from '../types';
import { PRES } from '../chrome';
import ColorField from './ColorField';
import EffectsControls from './EffectsControls';
import TypefaceSelect from './TypefaceSelect';
import { detectListKind, ListKind } from './listHtml';
import { TextStyleStep } from './layerList';
import TextStyleSelect from './TextStyleSelect';
import { LineDashToggle, TextAlignToggle, VerticalAlignToggle, toolbarToggleSx } from './toolbarIcons';
import { hasEditableText, isCroppableObject } from './shapeFrame';
import { TABLE_GRID, TABLE_HEADER_BG, TABLE_HEADER_COLOR } from './tableModel';
import {
  LeadingLooseIcon,
  LeadingTightIcon,
  SliderRow,
  ToolbarFlyout,
  TrackingLooseIcon,
  TrackingTightIcon,
  flyoutTriggerSx,
  isToolbarPointerTarget,
} from './ToolbarFlyout';

type FlyoutKind = 'fill' | 'text' | 'opacity' | 'line' | 'size' | 'tracking' | 'leading' | 'effects' | 'grid';

interface SelectionToolbarProps {
  object: SlideObject;
  disabled?: boolean;
  onStyle: (patch: Record<string, unknown>) => void;
  onToggleList?: (kind: ListKind) => void;
  onApplyTextStyle?: (step: TextStyleStep) => void;
  onReplaceImage?: () => void;
  onCropImage?: () => void;
  onFitImage?: () => void;
  onToggleMark?: (mark: 'bold' | 'italic' | 'underline') => void;
  queryTextMark?: (mark: 'bold' | 'italic' | 'underline') => boolean | null;
  queryTextStyle?: (key: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight') => { value: string | number | null; mixed: boolean };
}

const SelectionToolbar: React.FC<SelectionToolbarProps> = ({ object, disabled, onStyle, onToggleList, onApplyTextStyle, onReplaceImage, onCropImage, onFitImage, onToggleMark, queryTextMark, queryTextStyle }) => {
  const [flyout, setFlyout] = useState<FlyoutKind | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const displayedFontSize = (() => {
    const queried = queryTextStyle?.('fontSize');
    if (!queried?.mixed && typeof queried?.value === 'number' && Number.isFinite(queried.value)) {
      return queried.value;
    }
    return object.style?.fontSize || 24;
  })();
  const isText = hasEditableText(object);
  const isTable = object.type === 'table';
  const isShape = object.type === 'shape';
  const isLine = isShape && object.style?.shape === 'line';
  const closeFlyout = () => { setFlyout(null); setAnchorEl(null); };
  const toggleFlyout = (kind: FlyoutKind, event: React.MouseEvent<HTMLElement>) => {
    if (flyout === kind) {
      closeFlyout();
      return;
    }
    setFlyout(kind);
    setAnchorEl(event.currentTarget);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto', minWidth: 0, maxWidth: '100%' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, rowGap: 0.25, flexWrap: 'wrap', width: '100%', minWidth: 0 }}
        onMouseDown={(event) => {
          if (isToolbarPointerTarget(event.target)) return;
          event.preventDefault();
        }}
      >
          {isText && (
            <>
              <TextStyleSelect
                object={object}
                disabled={disabled}
                onChange={(step) => onApplyTextStyle?.(step)}
              />
              <IconButton
                size="small"
                disabled={disabled}
                title="Storlek"
                onClick={(event) => toggleFlyout('size', event)}
                sx={{ ...flyoutTriggerSx(flyout === 'size'), minWidth: 36, fontSize: 13, fontWeight: 700 }}
              >
                {displayedFontSize}
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled}
                title="Fet"
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (onToggleMark) onToggleMark('bold');
                  else onStyle({ fontWeight: queryTextMark?.('bold') === true ? 400 : 700 });
                }}
                sx={{ color: queryTextMark?.('bold') === true ? PRES.white : PRES.navy, bgcolor: queryTextMark?.('bold') === true ? PRES.navy : 'transparent', borderRadius: 1 }}
              >
                <FormatBoldIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled}
                title="Kursiv"
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (onToggleMark) onToggleMark('italic');
                  else onStyle({ fontStyle: queryTextMark?.('italic') === true ? 'normal' : 'italic' });
                }}
                sx={{ color: queryTextMark?.('italic') === true ? PRES.white : PRES.navy, bgcolor: queryTextMark?.('italic') === true ? PRES.navy : 'transparent', borderRadius: 1 }}
              >
                <FormatItalicIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled}
                title="Understruken"
                onPointerDown={(event) => event.preventDefault()}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (onToggleMark) onToggleMark('underline');
                  else onStyle({ textDecoration: queryTextMark?.('underline') === true ? 'none' : 'underline' });
                }}
                sx={{ color: queryTextMark?.('underline') === true ? PRES.white : PRES.navy, bgcolor: queryTextMark?.('underline') === true ? PRES.navy : 'transparent', borderRadius: 1 }}
              >
                <FormatUnderlinedIcon fontSize="small" />
              </IconButton>
              <TypefaceSelect
                disabled={disabled}
                mixed={Boolean(queryTextStyle?.('fontFamily')?.mixed || queryTextStyle?.('fontWeight')?.mixed)}
                style={{
                  fontFamily: typeof queryTextStyle?.('fontFamily')?.value === 'string'
                    ? String(queryTextStyle?.('fontFamily')?.value)
                    : object.style?.fontFamily,
                  fontWeight: typeof queryTextStyle?.('fontWeight')?.value === 'number'
                    ? Number(queryTextStyle?.('fontWeight')?.value)
                    : object.style?.fontWeight,
                }}
                onChange={(font) => onStyle({ fontFamily: font.family, fontWeight: font.fontWeight })}
              />
              <ToggleButtonGroup exclusive size="small" disabled={disabled} value={detectListKind(object.content)} onChange={(_event, value) => onToggleList?.(value || 'none')} sx={toolbarToggleSx}>
                <ToggleButton value="bullet" title="Punktlista"><FormatListBulletedIcon fontSize="small" /></ToggleButton>
                <ToggleButton value="ordered" title="Numrerad lista"><FormatListNumberedIcon fontSize="small" /></ToggleButton>
              </ToggleButtonGroup>
              <TextAlignToggle
                disabled={disabled}
                value={object.style?.textAlign || 'left'}
                onChange={(value) => onStyle({ textAlign: value })}
              />
              <VerticalAlignToggle
                disabled={disabled}
                value={object.style?.verticalAlign || 'top'}
                onChange={(value) => onStyle({ verticalAlign: value })}
              />
              <IconButton
                size="small"
                disabled={disabled}
                title="Spärrning"
                onClick={(event) => toggleFlyout('tracking', event)}
                sx={flyoutTriggerSx(flyout === 'tracking')}
              >
                <TrackingTightIcon />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled}
                title="Radavstånd"
                onClick={(event) => toggleFlyout('leading', event)}
                sx={flyoutTriggerSx(flyout === 'leading')}
              >
                <LeadingTightIcon />
              </IconButton>
              <ToggleButtonGroup exclusive size="small" disabled={disabled} value={object.style?.textTransform || 'none'} onChange={(_event, value) => value && onStyle({ textTransform: value })} sx={toolbarToggleSx}>
                <ToggleButton value="none" title="Normal" sx={{ fontSize: 11, textTransform: 'none' }}>Aa</ToggleButton>
                <ToggleButton value="uppercase" title="Versaler" sx={{ fontSize: 11 }}>AA</ToggleButton>
              </ToggleButtonGroup>
              <ColorToolButton kind="text" color={object.style?.color || '#1B3967'} title="Textfärg" disabled={disabled} active={flyout === 'text'} onClick={(event) => toggleFlyout('text', event)} />
              {!isShape && (
                <ColorToolButton kind="highlight" color={object.style?.backgroundColor || 'transparent'} foregroundColor={object.style?.color || PRES.textPrimary} title="Textbakgrund" disabled={disabled} active={flyout === 'fill'} onClick={(event) => toggleFlyout('fill', event)} />
              )}
            </>
          )}
          {isTable && (
            <>
              <TypefaceSelect
                disabled={disabled}
                style={object.style}
                onChange={(font) => onStyle({ fontFamily: font.family, fontWeight: font.fontWeight })}
              />
              <IconButton
                size="small"
                disabled={disabled}
                title="Storlek"
                onClick={(event) => toggleFlyout('size', event)}
                sx={{ ...flyoutTriggerSx(flyout === 'size'), minWidth: 36, fontSize: 13, fontWeight: 700 }}
              >
                {displayedFontSize}
              </IconButton>
              <TextAlignToggle
                disabled={disabled}
                value={object.style?.textAlign || 'left'}
                onChange={(value) => onStyle({ textAlign: value })}
              />
              <ColorToolButton kind="text" color={object.style?.color || TABLE_HEADER_COLOR} title={COPY.tableHeaderText} disabled={disabled} active={flyout === 'text'} onClick={(event) => toggleFlyout('text', event)} />
              <ColorToolButton kind="fill" color={object.style?.backgroundColor || TABLE_HEADER_BG} title={COPY.tableHeaderFill} disabled={disabled} active={flyout === 'fill'} onClick={(event) => toggleFlyout('fill', event)} />
              <ColorToolButton kind="grid" color={object.style?.strokeColor || TABLE_GRID} title={COPY.tableGrid} disabled={disabled} active={flyout === 'grid'} onClick={(event) => toggleFlyout('grid', event)} />
            </>
          )}
          {isShape && (
            <>
              {!isLine && <ColorToolButton kind="fill" color={object.style?.backgroundColor || '#1B3967'} title="Fyllnad" disabled={disabled} active={flyout === 'fill'} onClick={(event) => toggleFlyout('fill', event)} />}
              <IconButton size="small" title="Linje" onClick={(event) => toggleFlyout('line', event)} sx={flyoutTriggerSx(flyout === 'line')}>
                <LineWeightIcon fontSize="small" />
              </IconButton>
            </>
          )}
          {isCroppableObject(object) && (
            <>
              <IconButton size="small" disabled={disabled} title="Beskär" onClick={onCropImage} sx={{ color: PRES.textPrimary }}>
                <CropIcon fontSize="small" />
              </IconButton>
              {onFitImage && (
                <IconButton size="small" disabled={disabled} title={COPY.fitImageInFrame} onClick={onFitImage} sx={{ color: PRES.textPrimary }}>
                  <AspectRatioIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" disabled={disabled} title="Ersätt markerad bild/produkt" onClick={onReplaceImage} sx={{ color: PRES.textPrimary }}>
                <ImageOutlinedIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton size="small" title="Effekter" onClick={(event) => toggleFlyout('effects', event)} sx={flyoutTriggerSx(flyout === 'effects')}>
            <TuneOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" title="Transparens" onClick={(event) => toggleFlyout('opacity', event)} sx={flyoutTriggerSx(flyout === 'opacity')}>
            <OpacityIcon fontSize="small" />
          </IconButton>
        </Box>
        <ToolbarFlyout open={flyout === 'size'} anchorEl={anchorEl} onClose={closeFlyout} width={300}>
          <SliderRow
            label="Storlek"
            disabled={disabled}
            value={displayedFontSize}
            min={10}
            max={200}
            onChange={(value) => onStyle({ fontSize: value })}
          />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'tracking'} anchorEl={anchorEl} onClose={closeFlyout} width={300}>
          <SliderRow
            label="Spärrning"
            disabled={disabled}
            value={object.style?.letterSpacing ?? 0}
            min={0}
            max={14}
            step={0.5}
            startIcon={<TrackingTightIcon />}
            endIcon={<TrackingLooseIcon />}
            startTitle="Tätt"
            endTitle="Glest"
            onChange={(value) => onStyle({ letterSpacing: value })}
          />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'leading'} anchorEl={anchorEl} onClose={closeFlyout} width={300}>
          <SliderRow
            label="Radavstånd"
            disabled={disabled}
            value={resolvedLineHeight(object.style)}
            min={0.7}
            max={2.4}
            step={0.05}
            startIcon={<LeadingTightIcon />}
            endIcon={<LeadingLooseIcon />}
            startTitle="Tätt radavstånd"
            endTitle="Glest radavstånd"
            onChange={(value) => onStyle({ lineHeight: value })}
          />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'text'} anchorEl={anchorEl} onClose={closeFlyout} width={260}>
          <ColorField flush label={isTable ? COPY.tableHeaderText : 'Textfärg'} value={object.style?.color || (isTable ? TABLE_HEADER_COLOR : '#1B3967')} disabled={disabled} onChange={(color) => onStyle({ color })} />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'fill'} anchorEl={anchorEl} onClose={closeFlyout} width={260}>
          <ColorField flush label={isTable ? COPY.tableHeaderFill : object.type === 'text' ? 'Textbakgrund' : 'Fyllnad'} value={object.style?.backgroundColor || (isTable ? TABLE_HEADER_BG : object.type === 'text' ? 'transparent' : '#1B3967')} allowNone={!isTable} disabled={disabled} onChange={(color) => onStyle({ backgroundColor: color })} />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'grid'} anchorEl={anchorEl} onClose={closeFlyout} width={280}>
          <Box sx={{ mb: 1.75 }}>
            <ColorField flush label={COPY.tableGrid} value={object.style?.strokeColor || TABLE_GRID} disabled={disabled} onChange={(color) => onStyle({ strokeColor: color })} />
          </Box>
          <SliderRow
            label={COPY.tableStrokeWidth}
            disabled={disabled}
            value={object.style?.strokeWidth ?? 1}
            min={0.5}
            max={8}
            step={0.5}
            onChange={(value) => onStyle({ strokeWidth: value })}
          />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'line'} anchorEl={anchorEl} onClose={closeFlyout} width={300}>
          <Box sx={{ mb: 1.75 }}>
            <LineDashToggle
              disabled={disabled}
              value={object.style?.strokeDash || 'solid'}
              onChange={(value) => onStyle({ strokeDash: value })}
            />
          </Box>
          <Box sx={{ mb: 1.75 }}>
            <SliderRow
              label="Linjens tjocklek"
              disabled={disabled}
              value={object.style?.strokeWidth ?? (isLine ? 3 : 0)}
              min={0}
              max={40}
              onChange={(value) => onStyle({ strokeWidth: value })}
            />
          </Box>
          <ColorField flush label="Linjefärg" value={object.style?.strokeColor || '#1B3967'} disabled={disabled} onChange={(color) => onStyle({ strokeColor: color })} />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'effects'} anchorEl={anchorEl} onClose={closeFlyout} width={300}>
          <EffectsControls object={object} disabled={disabled} onStyle={onStyle} />
        </ToolbarFlyout>
        <ToolbarFlyout open={flyout === 'opacity'} anchorEl={anchorEl} onClose={closeFlyout} width={280}>
          <SliderRow
            label="Transparens"
            disabled={disabled}
            value={Math.round((object.style?.opacity ?? 1) * 100)}
            min={10}
            max={100}
            onChange={(value) => onStyle({ opacity: value / 100 })}
          />
        </ToolbarFlyout>
      </Box>
  );
};

const ColorToolButton: React.FC<{
  kind: 'text' | 'highlight' | 'fill' | 'grid';
  color: string;
  foregroundColor?: string;
  title?: string;
  disabled?: boolean;
  active?: boolean;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}> = ({ kind, color, foregroundColor, title, disabled, active, onClick }) => (
  <IconButton
    size="small"
    title={title || 'Färg'}
    aria-label={title || 'Färg'}
    disabled={disabled}
    onClick={onClick}
    sx={flyoutTriggerSx(Boolean(active))}
  >
    <Box sx={{ position: 'relative', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {kind === 'text' && <FormatColorTextIcon sx={{ fontSize: 18, color: PRES.textPrimary }} />}
      {kind === 'fill' && <FormatColorFillIcon sx={{ fontSize: 18, color: PRES.textPrimary }} />}
      {kind === 'grid' && <BorderAllIcon sx={{ fontSize: 18, color: PRES.textPrimary }} />}
      {kind === 'highlight' && (
        <Box
          component="span"
          sx={{
            width: 17,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `1px solid ${PRES.borderStrong}`,
            borderRadius: 0.5,
            bgcolor: color === 'transparent' ? PRES.white : color,
            backgroundImage: color === 'transparent'
              ? 'linear-gradient(135deg, transparent 46%, #c0392b 48%, #c0392b 52%, transparent 54%)'
              : 'none',
            color: foregroundColor || PRES.textPrimary,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          A
        </Box>
      )}
      {kind !== 'highlight' && (
      <Box
        sx={{
          position: 'absolute',
          left: 1,
          right: 1,
          bottom: -2,
          height: 4,
          borderRadius: 0.5,
          border: `1px solid ${PRES.borderStrong}`,
          bgcolor: color === 'transparent' ? PRES.white : color,
          backgroundImage: color === 'transparent' ? 'linear-gradient(135deg, transparent 45%, #c00 50%, transparent 55%)' : 'none',
        }}
      />
      )}
    </Box>
  </IconButton>
);

export default SelectionToolbar;
